import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Heart,
  Navigation,
  Bookmark,
  Share2,
  Calendar,
  Clock,
  Tag,
  Shield,
  Info,
} from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { STRIPE, TYPE_META } from '@/data/mockData'
import { mapPostRow } from '@/lib/mapPost'
import PostCard from '@/components/features/feed/PostCard'
import CommentsSection from '@/components/features/post/CommentsSection'
import { useAuthStore } from '@/store/auth.store'
import { useMyInterests, useSavedPosts } from '@/hooks/useFeed'
import { useToggleInterest, useToggleSave } from '@/hooks/usePosts'
import { toast } from '@/components/ui/Toast'

const C = {
  teal: '#0F5C5C',
  terra: '#D9785B',
  amber: '#D3A34A',
  ink: '#202523',
  ink2: '#6D7471',
  border: '#E1E5E3',
  grayBg: '#F1F3F2',
}
const FH = "'Plus Jakarta Sans', sans-serif"
const FB = "'Inter', sans-serif"

interface BusinessRow {
  id: string
  name: string
  category: string
  zone: string
  verified: boolean
  cover_colors: [string, string]
  emoji: string
  address: string | null
}

interface PostRow {
  id: string
  business_id: string
  type: string
  title: string
  description: string | null
  category: string | null
  scheduled_date: string | null
  start_time: string | null
  price_info: string | null
  min_age: string | null
  conditions: string | null
  mood: string | null
  occupancy: string | null
  interested_count: number
  allow_comments: boolean
  business: BusinessRow
}

export default function PostPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const { data: post, isLoading } = useQuery({
    queryKey: ['post', id],
    queryFn: async () => {
      if (!supabase || !id) return null
      const { data, error } = await supabase
        .from('posts')
        .select(
          '*, business:businesses(id, name, category, zone, verified, cover_colors, emoji, address)'
        )
        .eq('id', id)
        .single()
      if (error) return null
      return data as unknown as PostRow
    },
    enabled: !!id,
  })

  const { data: relatedRows } = useQuery({
    queryKey: ['post-related', post?.category, id],
    queryFn: async () => {
      if (!supabase || !post) return []
      const { data } = await supabase
        .from('posts')
        .select('*, business:businesses(name, zone, verified, cover_colors, emoji)')
        .eq('is_active', true)
        .eq('category', post.category ?? '')
        .neq('id', post.id)
        .limit(3)
      return data ?? []
    },
    enabled: !!post,
  })

  const myInterests = useMyInterests()
  const { data: savedData } = useSavedPosts()
  const toggleInterest = useToggleInterest(id ?? '')
  const toggleSave = useToggleSave(id ?? '')

  const [interested, setInterested] = useState(false)
  const [saved, setSaved] = useState(false)

  useEffect(() => setInterested(myInterests.has(id ?? '')), [myInterests, id])
  useEffect(() => {
    setSaved((savedData?.data ?? []).some((row) => row.id === id))
  }, [savedData, id])

  const requireAuth = (action: string) => {
    if (!user) {
      toast.info(`Inicia sesión para ${action}`)
      return false
    }
    return true
  }

  const infoRow = (icon: React.ReactNode, label: string, value: string) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 0',
        borderBottom: `1px solid ${C.border}`,
      }}
    >
      {icon}
      <span style={{ flex: 1, fontSize: 14, color: C.ink2 }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600 }}>{value}</span>
    </div>
  )

  if (isLoading) {
    return (
      <div className="page-sm">
        <div
          style={{
            height: 320,
            borderRadius: 20,
            marginBottom: 24,
            background: C.grayBg,
            animation: 'pxPulse 1.5s infinite',
          }}
        />
        <div
          style={{
            height: 24,
            width: '60%',
            borderRadius: 8,
            marginBottom: 12,
            background: C.grayBg,
            animation: 'pxPulse 1.5s infinite',
          }}
        />
        <div
          style={{
            height: 16,
            width: '90%',
            borderRadius: 8,
            background: C.grayBg,
            animation: 'pxPulse 1.5s infinite',
          }}
        />
      </div>
    )
  }

  if (!post) {
    return (
      <div className="page-sm" style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ fontFamily: FH, fontWeight: 800, fontSize: 22, marginBottom: 10 }}>
          No encontramos esta publicación
        </div>
        <div style={{ color: C.ink2, marginBottom: 20 }}>
          Puede que ya no esté activa o el enlace esté roto.
        </div>
        <button
          onClick={() => navigate('/')}
          style={{
            padding: '12px 22px',
            borderRadius: 12,
            border: 'none',
            background: C.teal,
            color: '#fff',
            fontFamily: FB,
            fontWeight: 700,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Volver al inicio
        </button>
      </div>
    )
  }

  const business = post.business
  const p = mapPostRow(post as unknown as Record<string, unknown>, business)
  const tm = TYPE_META[p.type] ?? { label: p.type, color: C.teal }
  const related = (relatedRows ?? []).map((r) =>
    mapPostRow(r as unknown as Record<string, unknown>, r.business as unknown as BusinessRow)
  )

  return (
    <div className="page-sm">
      <button
        onClick={() => navigate(-1)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: C.ink2,
          fontFamily: FB,
          fontSize: 14,
          marginBottom: 20,
        }}
      >
        <ArrowLeft size={18} /> Volver
      </button>

      <div className="two-col">
        {/* Izquierda */}
        <div>
          {/* Imagen hero */}
          <div
            style={{
              height: 320,
              background: STRIPE(p.img[0], p.img[1]),
              borderRadius: 20,
              marginBottom: 24,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: 16,
                left: 16,
                background: tm.color,
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
                padding: '7px 13px',
                borderRadius: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 7,
              }}
            >
              {p.type === 'live' && (
                <span
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: 99,
                    background: '#fff',
                    animation: 'pxPulse 1.3s infinite',
                  }}
                />
              )}
              {tm.label}
            </div>
          </div>

          {/* Negocio */}
          <button
            onClick={() => navigate(`/biz/${business.id}`)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              background: 'transparent',
              border: 'none',
              padding: '0 0 16px',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
            }}
          >
            <div
              style={{
                width: 46,
                height: 46,
                borderRadius: 12,
                background: STRIPE(business.cover_colors[0], business.cover_colors[1]),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 22,
                flexShrink: 0,
              }}
            >
              {business.emoji}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontFamily: FH, fontWeight: 700, fontSize: 16 }}>
                  {business.name}
                </span>
                {business.verified && <span style={{ color: C.teal }}>✓</span>}
              </div>
              <div style={{ fontSize: 13, color: C.ink2 }}>
                {business.category} · {business.zone}
              </div>
            </div>
            <span style={{ color: C.ink2 }}>›</span>
          </button>

          <h1
            style={{
              fontFamily: FH,
              fontWeight: 800,
              fontSize: 26,
              lineHeight: 1.2,
              margin: '0 0 14px',
            }}
          >
            {p.title}
          </h1>
          <p style={{ fontSize: 15.5, color: C.ink, lineHeight: 1.6, margin: '0 0 20px' }}>
            {p.desc}
          </p>

          {p.cond && (
            <div
              style={{
                fontSize: 13.5,
                color: C.ink2,
                background: `${C.amber}12`,
                borderRadius: 12,
                padding: '12px 15px',
                marginBottom: 20,
                display: 'flex',
                gap: 9,
              }}
            >
              <Info size={16} color={C.amber} style={{ flexShrink: 0, marginTop: 1 }} />{' '}
              Condiciones: {p.cond}
            </div>
          )}

          {(p.date || p.start || p.price || p.age) && (
            <>
              <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 16, marginBottom: 4 }}>
                Información
              </div>
              {p.date && infoRow(<Calendar size={17} color={C.ink2} />, 'Fecha', p.date)}
              {p.start && infoRow(<Clock size={17} color={C.ink2} />, 'Hora', p.start)}
              {p.price && infoRow(<Tag size={17} color={C.ink2} />, 'Precio', p.price)}
              {p.age && infoRow(<Shield size={17} color={C.ink2} />, 'Edad mínima', p.age)}
            </>
          )}

          {/* Comentarios */}
          <CommentsSection postId={post.id} allowComments={post.allow_comments} />

          {/* Relacionadas */}
          {related.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 16, marginBottom: 14 }}>
                Actividades relacionadas
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))',
                  gap: 14,
                }}
              >
                {related.map((r) => (
                  <PostCard key={r.id} post={r} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Derecha — acciones fijas */}
        <div
          style={{ position: 'sticky', top: 20, display: 'flex', flexDirection: 'column', gap: 16 }}
        >
          <div
            style={{
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 18,
              padding: 20,
            }}
          >
            {/* Social proof */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                background: C.grayBg,
                borderRadius: 12,
                marginBottom: 16,
              }}
            >
              <div style={{ display: 'flex' }}>
                {[C.teal, C.terra, C.amber, '#153D3D'].map((c, i) => (
                  <div
                    key={i}
                    style={{
                      width: 26,
                      height: 26,
                      borderRadius: 99,
                      background: c,
                      border: `2px solid ${C.grayBg}`,
                      marginLeft: i ? -9 : 0,
                    }}
                  />
                ))}
              </div>
              <div style={{ flex: 1, fontSize: 13.5, fontWeight: 700 }}>
                {p.interested} personas interesadas
              </div>
            </div>

            <button
              onClick={() => {
                if (!requireAuth('registrar interés')) return
                const next = !interested
                setInterested(next)
                toggleInterest.mutate(next, { onError: () => setInterested(!next) })
              }}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '13px',
                borderRadius: 12,
                border: `1px solid ${interested ? C.terra : C.border}`,
                background: interested ? C.terra : '#fff',
                color: interested ? '#fff' : C.ink,
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                marginBottom: 10,
              }}
            >
              <Heart size={18} fill={interested ? '#fff' : 'none'} />
              {interested ? 'Te interesa' : 'Me interesa'}
            </button>

            <button
              onClick={() => toast.info('Abriendo ruta…')}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                padding: '13px',
                borderRadius: 12,
                border: 'none',
                background: C.teal,
                color: '#fff',
                fontFamily: FB,
                fontWeight: 700,
                fontSize: 15,
                cursor: 'pointer',
                marginBottom: 10,
              }}
            >
              <Navigation size={18} /> Cómo llegar
            </button>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => {
                  if (!requireAuth('guardar')) return
                  const next = !saved
                  setSaved(next)
                  toggleSave.mutate(next, { onError: () => setSaved(!next) })
                }}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  padding: '11px',
                  borderRadius: 11,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  fontFamily: FB,
                  fontWeight: 600,
                  fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                <Bookmark
                  size={16}
                  color={saved ? C.teal : C.ink2}
                  fill={saved ? C.teal : 'none'}
                />
                {saved ? 'Guardado' : 'Guardar'}
              </button>
              <button
                onClick={() => toast.info('Enlace copiado')}
                style={{
                  flex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 7,
                  padding: '11px',
                  borderRadius: 11,
                  border: `1px solid ${C.border}`,
                  background: '#fff',
                  fontFamily: FB,
                  fontWeight: 600,
                  fontSize: 13.5,
                  cursor: 'pointer',
                }}
              >
                <Share2 size={16} /> Compartir
              </button>
            </div>
          </div>

          {/* Mapa de ubicación */}
          <div
            style={{
              background: '#fff',
              border: `1px solid ${C.border}`,
              borderRadius: 18,
              padding: 16,
            }}
          >
            <div style={{ fontFamily: FH, fontWeight: 700, fontSize: 15, marginBottom: 10 }}>
              Ubicación
            </div>
            <div
              style={{
                height: 140,
                borderRadius: 12,
                overflow: 'hidden',
                border: `1px solid ${C.border}`,
                marginBottom: 12,
                background: 'linear-gradient(180deg,#dfe9e7,#eef2f0)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: 30,
              }}
            >
              🗺️
            </div>
            <div style={{ fontSize: 13.5, color: C.ink2, marginBottom: 12 }}>
              {business.address ?? `${business.name}, ${business.zone}`}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['Google Maps', 'Waze'].map((app) => (
                <button
                  key={app}
                  onClick={() => toast.info(`Abriendo ${app}`)}
                  style={{
                    flex: 1,
                    padding: '9px',
                    borderRadius: 9,
                    border: `1px solid ${C.border}`,
                    background: '#fff',
                    fontFamily: FB,
                    fontWeight: 700,
                    fontSize: 13,
                    cursor: 'pointer',
                  }}
                >
                  {app}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
